// banking/src/pages/onboarding/corporate/CorporateOnboarding.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTranslation, type Language } from './corporate-translations';
import { getCurrentApiUrl } from '../../../config/server.config';
import Logo from '../../../assets/images/logonegro.svg';

// Steps components (to be created)
import Step1CompanyInfo from './steps/Step1_CompanyInfo';
import Step2CompanyAddress from './steps/Step2_CompanyAddress';
import Step3CompanyDocuments from './steps/Step3_CompanyDocuments';
import Step4Representatives from './steps/Step4_Representatives';
import Step5RepresentativeForm from './steps/Step5_RepresentativeForm';
import Step6RepresentativeDocuments from './steps/Step6_RepresentativeDocuments';
import Step7ReviewAndSubmit from './steps/Step7_ReviewAndSubmit';

// Types
export interface RepresentativeDocument {
  idCardFront?: File;
  idCardBack?: File;
  passport?: File;
  drivingLicenseFront?: File;
  drivingLicenseBack?: File;
  selfieWithId?: File;
  proofOfAddress?: File;
  bankStatement?: File;
}

export interface Representative {
  // Database ID (when editing existing representative)
  id?: string;

  // Relationship
  relationshipType: string;
  role: string;
  isUltimateBeneficiary: boolean;
  ownershipPercentage: number;
  representationStartDate: string;

  // Personal Data
  firstName: string;
  lastName: string;
  secondLastName: string;
  middleName?: string;
  gender: string;
  birthDate: string;
  birthCountry: string;
  nationality: string;
  citizenship: string;
  residenceCountry: string;
  secondNationality?: string;

  // Document
  documentType: string;
  documentNumber: string;
  documentCountry: string;
  documentExpiry: string;

  // Contact
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;

  // Address
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;

  // Employment/Tax
  employmentStatus: string;
  occupation: string; // SOC code
  occupationCode?: string;
  employerName: string;
  jobTitle: string;
  taxCountry: string;
  taxNumber?: string;
  incomeSource?: string;
  monthlyIncomeRange?: string;
  isPep?: boolean; // Politically Exposed Person

  // Documents
  documents: RepresentativeDocument;
}

export interface CorporateFormData {
  // Step 1: Company Basic Info
  companyName: string;
  legalName: string;
  companyType: string;
  taxId: string;
  taxCountry: string;
  registrationNumber: string;
  registrationCountry: string;
  registrationDate: string;
  incorporationDate: string;
  businessSectorCode: string; // NAICS
  businessActivity: string;
  businessType?: string;
  businessDescription?: string;
  businessPurpose?: string;
  industryType?: string;

  // Step 2: Company Contact & Address
  companyEmail: string;
  companyPhoneCountryCode: string;
  companyPhoneNumber: string;
  companyPhoneExtension?: string;
  addressLine1: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  website?: string;

  // Step 3: Company Documents
  businessRegistration?: File;
  articlesOfIncorporation?: File;
  businessUtilityBill?: File;

  // Step 4-6: Representatives
  representatives: Representative[];
  currentRepresentativeIndex: number;
}

const CorporateOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [language, setLanguage] = useState<Language>('en');
  const [languageLoading, setLanguageLoading] = useState<boolean>(true); // ← NUEVO: Estado de carga del idioma
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CorporateFormData>({
    // Company
    companyName: '',
    legalName: '',
    companyType: '',
    taxId: '',
    taxCountry: '',
    registrationNumber: '',
    registrationCountry: '',
    registrationDate: '',
    incorporationDate: '',
    businessSectorCode: '',
    businessActivity: '',

    // Contact
    companyEmail: '',
    companyPhoneCountryCode: '',
    companyPhoneNumber: '',
    addressLine1: '',

    // Representatives
    representatives: [],
    currentRepresentativeIndex: -1,
  });

  // State to track if user has finalized representatives (Step 4)
  const [representativesFinalized, setRepresentativesFinalized] = useState<boolean>(false);

  // State to track if user has accepted terms (Step 7)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  // 🔒 Flag to prevent loadProgress from running if complete
  const [isCompleteAndRedirecting, setIsCompleteAndRedirecting] = useState<boolean>(false);

  // Load language AND check if onboarding is complete
  useEffect(() => {
    const loadLanguageAndCheckStatus = async () => {
      setLanguageLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️ No token found, using default language');
        setLanguage('en');
        setLanguageLoading(false);
        return;
      }

      try {
        // ALWAYS call backend to check completion status
        console.log('🔍 Llamando a /corporate/status...');
        const response = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('📊 Respuesta de /corporate/status:', result);

          // 🔥 CRITICAL FIX: Check if onboarding is complete FIRST
          if (result.success && result.data.isComplete && result.data.currentStep === -1) {
            console.log('✅ Onboarding already complete, redirecting NOW');
            setIsCompleteAndRedirecting(true); // 🔒 Set flag BEFORE redirect
            window.location.href = '/dashboard';
            return; // Exit immediately
          }

          // Onboarding NOT complete - load language
          if (result.success && result.data.language) {
            console.log('✅ Idioma cargado desde BD:', result.data.language);
            setLanguage(result.data.language as Language);
            localStorage.setItem('corporateOnboardingLanguage', result.data.language);
          } else {
            // Fallback to localStorage
            const savedLang = localStorage.getItem('corporateOnboardingLanguage') as Language;
            if (savedLang) {
              console.log('✅ Idioma cargado desde localStorage:', savedLang);
              setLanguage(savedLang);
            } else {
              setLanguage('en');
            }
          }

          setLanguageLoading(false);
        } else {
          console.error('❌ Error al llamar /corporate/status:', response.status);
          // Fallback to localStorage
          const savedLang = localStorage.getItem('corporateOnboardingLanguage') as Language;
          setLanguage(savedLang || 'en');
          setLanguageLoading(false);
        }
      } catch (error) {
        console.error('❌ Error loading status from backend:', error);
        // Fallback to localStorage
        const savedLang = localStorage.getItem('corporateOnboardingLanguage') as Language;
        setLanguage(savedLang || 'en');
        setLanguageLoading(false);
      }
    };

    loadLanguageAndCheckStatus();
  }, [navigate]);

  // Load progress on mount (ONLY if not redirecting)
  useEffect(() => {
    if (!isCompleteAndRedirecting) {
      loadProgress();
    }
  }, [isCompleteAndRedirecting]);

  // Auto-save progress when formData or currentStep changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentStep > 1) {
        saveProgress();
      }
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => clearTimeout(timer);
  }, [formData, currentStep]);

  // When currentRepresentativeIndex changes to valid index and we're on step 4, go to step 5
  useEffect(() => {
    if (currentStep === 4 && formData.currentRepresentativeIndex >= 0) {
      setCurrentStep(5);
    }
  }, [formData.currentRepresentativeIndex]);

  // REMOVED AUTO-NAVIGATION FROM STEP 5 TO 6
  // User must click "Save" button manually to advance

  // When in step 6 and currentRepresentativeIndex changes to -1, go back to step 4 (add another representative)
  useEffect(() => {
    if (currentStep === 6 && formData.currentRepresentativeIndex === -1) {
      setCurrentStep(4);
      setRepresentativesFinalized(false); // 🔓 Reset finalized state to allow editing
    }
  }, [formData.currentRepresentativeIndex, currentStep]);

  const loadProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found, skipping progress load');
        return;
      }

      console.log('🔄 Loading progress from database...');

      const response = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/progress`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();

        // New structure: { success, data: { currentStep, formData, hasProgress } }
        if (result.success && result.data && result.data.formData) {
          const loadedFormData = result.data.formData;
          const currentStepFromDB = result.data.currentStep || 1;

          console.log('✅ Progress loaded from database:', {
            currentStep: currentStepFromDB,
            hasCompanyName: !!loadedFormData.companyName,
            hasCompanyEmail: !!loadedFormData.companyEmail,
            hasBusinessRegistration: !!loadedFormData.businessRegistration,
            representativesCount: loadedFormData.representatives?.length || 0
          });

          setFormData(loadedFormData);
          setCurrentStep(currentStepFromDB);
        }
      } else if (response.status === 404) {
        console.log('No previous progress found, starting fresh');
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const saveProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Serialize formData: convert File objects to metadata
      const serializableFormData = {
        ...formData,
        // Company documents (Step 3) - keep only metadata
        businessRegistration: formData.businessRegistration ? {
          name: formData.businessRegistration.name,
          type: formData.businessRegistration.type,
          size: formData.businessRegistration.size,
          _isFile: true
        } : undefined,
        articlesOfIncorporation: formData.articlesOfIncorporation ? {
          name: formData.articlesOfIncorporation.name,
          type: formData.articlesOfIncorporation.type,
          size: formData.articlesOfIncorporation.size,
          _isFile: true
        } : undefined,
        businessUtilityBill: formData.businessUtilityBill ? {
          name: formData.businessUtilityBill.name,
          type: formData.businessUtilityBill.type,
          size: formData.businessUtilityBill.size,
          _isFile: true
        } : undefined,
        // Representatives documents (Step 6) - keep only metadata
        representatives: formData.representatives.map(rep => ({
          ...rep,
          documents: {
            idCardFront: rep.documents.idCardFront ? {
              name: rep.documents.idCardFront.name,
              type: rep.documents.idCardFront.type,
              size: rep.documents.idCardFront.size,
              _isFile: true
            } : undefined,
            idCardBack: rep.documents.idCardBack ? {
              name: rep.documents.idCardBack.name,
              type: rep.documents.idCardBack.type,
              size: rep.documents.idCardBack.size,
              _isFile: true
            } : undefined,
            passport: rep.documents.passport ? {
              name: rep.documents.passport.name,
              type: rep.documents.passport.type,
              size: rep.documents.passport.size,
              _isFile: true
            } : undefined,
            selfieWithId: rep.documents.selfieWithId ? {
              name: rep.documents.selfieWithId.name,
              type: rep.documents.selfieWithId.type,
              size: rep.documents.selfieWithId.size,
              _isFile: true
            } : undefined,
            proofOfAddress: rep.documents.proofOfAddress ? {
              name: rep.documents.proofOfAddress.name,
              type: rep.documents.proofOfAddress.type,
              size: rep.documents.proofOfAddress.size,
              _isFile: true
            } : undefined,
            bankStatement: rep.documents.bankStatement ? {
              name: rep.documents.bankStatement.name,
              type: rep.documents.bankStatement.type,
              size: rep.documents.bankStatement.size,
              _isFile: true
            } : undefined,
          }
        }))
      };

      await fetch(`${getCurrentApiUrl()}/onboarding/corporate/save-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: 'CORPORATE', // ✅ REQUIRED by backend
          currentStep,
          formData: serializableFormData
        })
      });

      console.log('✅ Progress saved');
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const updateFormData = (updates: Partial<CorporateFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const validateCurrentStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    switch (currentStep) {
      case 1: // Company Basic Info
        if (!formData.companyName) newErrors.companyName = getTranslation('required_field', language);
        if (!formData.legalName) newErrors.legalName = getTranslation('required_field', language);
        if (!formData.companyType) newErrors.companyType = getTranslation('required_field', language);
        if (!formData.taxId) newErrors.taxId = getTranslation('required_field', language);
        if (!formData.taxCountry) newErrors.taxCountry = getTranslation('required_field', language);
        if (!formData.registrationNumber) newErrors.registrationNumber = getTranslation('required_field', language);
        if (!formData.registrationCountry) newErrors.registrationCountry = getTranslation('required_field', language);
        if (!formData.registrationDate) newErrors.registrationDate = getTranslation('required_field', language);
        if (!formData.incorporationDate) newErrors.incorporationDate = getTranslation('required_field', language);
        if (!formData.businessSectorCode) newErrors.businessSectorCode = getTranslation('required_field', language);
        if (!formData.businessActivity) newErrors.businessActivity = getTranslation('required_field', language);
        if (!formData.businessType) newErrors.businessType = getTranslation('required_field', language);
        break;

      case 2: // Company Contact & Address
        if (!formData.companyEmail) {
          newErrors.companyEmail = getTranslation('required_field', language);
        } else if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) {
          newErrors.companyEmail = getTranslation('invalid_email', language);
        }
        if (!formData.companyPhoneCountryCode) newErrors.companyPhoneCountryCode = getTranslation('required_field', language);
        if (!formData.companyPhoneNumber) newErrors.companyPhoneNumber = getTranslation('required_field', language);
        if (!formData.addressLine1) newErrors.addressLine1 = getTranslation('required_field', language);
        if (!formData.country) newErrors.country = getTranslation('required_field', language);
        if (!formData.city) newErrors.city = getTranslation('required_field', language);
        if (!formData.state) newErrors.state = getTranslation('required_field', language);
        if (!formData.postalCode) newErrors.postalCode = getTranslation('required_field', language);
        // website is optional - no validation
        break;

      case 3: // Company Documents
        if (!formData.businessRegistration) newErrors.businessRegistration = getTranslation('required_field', language);
        if (!formData.articlesOfIncorporation) newErrors.articlesOfIncorporation = getTranslation('required_field', language);
        if (!formData.businessUtilityBill) newErrors.businessUtilityBill = getTranslation('required_field', language);
        break;

      case 4: // Representatives
        if (formData.representatives.length === 0) {
          newErrors.representatives = getTranslation('min_one_representative', language);
        }
        break;

      case 7: // Review and submit
        // All validations should have been done in previous steps
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    // Save to actual database tables (Steps 1-2, 5)
    // Step 5 auto-saves representative to formData via useEffect, so it's ready here
    await saveStepToDatabase();

    // Also save progress metadata for recovery
    await saveProgress();

    // Special case: Step 4 (Representatives) with finalized → jump to Step 7
    if (currentStep === 4 && representativesFinalized) {
      setCurrentStep(7);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      await handleSubmit();
    }
  };

  const saveStepToDatabase = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Steps 1-2: Save company info to database
      if (currentStep === 1 || currentStep === 2) {
        console.log(`💾 Saving step ${currentStep} to database...`);

        const response = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/save-step`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            currentStep,
            formData
          })
        });

        if (!response.ok) {
          console.error('❌ Failed to save step to database');
        } else {
          console.log('✅ Step saved to database');
        }
      }

      // Step 4: Save ALL representatives to database (when finalized)
      if (currentStep === 4 && representativesFinalized && formData.representatives.length > 0) {
        console.log(`💾 Saving ${formData.representatives.length} representatives to database...`);

        const response = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/save-representatives`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            representatives: formData.representatives
          })
        });

        if (!response.ok) {
          console.error('❌ Failed to save representatives to database');
          const errorData = await response.json();
          console.error('Error details:', errorData);
        } else {
          const result = await response.json();
          console.log('✅ Representatives saved to database:', result);
        }
      }

      // Step 3: Upload company documents to S3
      if (currentStep === 3) {
        console.log('📤 Uploading company documents...');

        // Only upload if it's a new File (not previously uploaded metadata)
        if (formData.businessRegistration && formData.businessRegistration instanceof File) {
          await uploadDocument(formData.businessRegistration, 'businessRegistration', 3);
        }
        if (formData.articlesOfIncorporation && formData.articlesOfIncorporation instanceof File) {
          await uploadDocument(formData.articlesOfIncorporation, 'articlesOfIncorporation', 3);
        }
        if (formData.businessUtilityBill && formData.businessUtilityBill instanceof File) {
          await uploadDocument(formData.businessUtilityBill, 'proofOfAddress', 3);
        }

        console.log('✅ Company documents uploaded');
      }

      // Step 6: Upload representative documents to S3
      if (currentStep === 6 && formData.currentRepresentativeIndex !== -1) {
        console.log('📤 Uploading representative documents...');

        const rep = formData.representatives[formData.currentRepresentativeIndex];
        if (rep) {
          // Only upload if it's a new File (not previously uploaded metadata)
          if (rep.documents.passport && rep.documents.passport instanceof File) {
            await uploadDocument(rep.documents.passport, 'passport', 6, formData.currentRepresentativeIndex);
          }
          if (rep.documents.idCardFront && rep.documents.idCardFront instanceof File) {
            await uploadDocument(rep.documents.idCardFront, 'idCardFront', 6, formData.currentRepresentativeIndex);
          }
          if (rep.documents.idCardBack && rep.documents.idCardBack instanceof File) {
            await uploadDocument(rep.documents.idCardBack, 'idCardBack', 6, formData.currentRepresentativeIndex);
          }
          if (rep.documents.selfieWithId && rep.documents.selfieWithId instanceof File) {
            await uploadDocument(rep.documents.selfieWithId, 'selfie', 6, formData.currentRepresentativeIndex);
          }
          if (rep.documents.proofOfAddress && rep.documents.proofOfAddress instanceof File) {
            await uploadDocument(rep.documents.proofOfAddress, 'proofOfAddress', 6, formData.currentRepresentativeIndex);
          }
          if (rep.documents.bankStatement && rep.documents.bankStatement instanceof File) {
            await uploadDocument(rep.documents.bankStatement, 'bankStatement', 6, formData.currentRepresentativeIndex);
          }

          console.log('✅ Representative documents uploaded');
        }
      }
    } catch (error) {
      console.error('Error saving step to database:', error);
    }
  };

  const uploadDocument = async (file: File, documentType: string, step: number, representativeIndex?: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', documentType);
      formData.append('currentStep', step.toString());
      if (representativeIndex !== undefined) {
        formData.append('representativeIndex', representativeIndex.toString());
      }

      console.log(`📤 Uploading ${documentType}, file size: ${file.size} bytes`);

      const response = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/upload-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        },
        body: formData
      });

      console.log(`📥 Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Failed to upload ${documentType}:`, errorText);
        throw new Error(`Failed to upload ${documentType}: ${errorText}`);
      } else {
        const result = await response.json();
        console.log(`✅ Uploaded ${documentType}:`, result.data.s3Url);

        // Update formData with uploaded metadata
        updateFormData({
          [documentType]: {
            name: file.name,
            url: result.data.s3Url,
            _isUploaded: true
          }
        });
      }
    } catch (error) {
      console.error(`Error uploading ${documentType}:`, error);
      throw error;
    }
  };

  // Step 3: Company document upload callbacks
  const uploadBusinessRegistration = async (file: File) => {
    await uploadDocument(file, 'businessRegistration', 3);
  };

  const uploadArticlesOfIncorporation = async (file: File) => {
    await uploadDocument(file, 'articlesOfIncorporation', 3);
  };

  const uploadBusinessUtilityBill = async (file: File) => {
    await uploadDocument(file, 'businessUtilityBill', 3);
  };

  // Step 6: Representative document upload callbacks
  const uploadRepresentativeDocument = async (file: File, documentType: string, repIndex: number) => {
    await uploadDocument(file, documentType, 6, repIndex);
  };

  // Check if all Step 3 documents are uploaded
  const isStep3Complete = () => {
    if (currentStep !== 3) return true; // Not on Step 3, don't block

    const hasBusinessRegistration = formData.businessRegistration &&
      (formData.businessRegistration instanceof File || (formData.businessRegistration as any)._isUploaded);
    const hasArticlesOfIncorporation = formData.articlesOfIncorporation &&
      (formData.articlesOfIncorporation instanceof File || (formData.articlesOfIncorporation as any)._isUploaded);
    const hasBusinessUtilityBill = formData.businessUtilityBill &&
      (formData.businessUtilityBill instanceof File || (formData.businessUtilityBill as any)._isUploaded);

    return hasBusinessRegistration && hasArticlesOfIncorporation && hasBusinessUtilityBill;
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // 🔓 Reset representativesFinalized when going back to Step 4
      if (currentStep === 5 || currentStep === 6 || currentStep === 7) {
        const targetStep = currentStep - 1;
        if (targetStep === 4) {
          setRepresentativesFinalized(false);
        }
      }

      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    console.log('🚀 Starting corporate onboarding submission...');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        throw new Error('No authentication token found');
      }

      console.log('✅ Token found, preparing form data...');

      // Prepare JSON data (documents already uploaded to S3 in previous steps)
      const submitData = {
        // Company information
        companyName: formData.companyName,
        legalName: formData.legalName,
        companyType: formData.companyType,
        taxId: formData.taxId,
        taxCountry: formData.taxCountry,
        registrationNumber: formData.registrationNumber,
        registrationCountry: formData.registrationCountry,
        registrationDate: formData.registrationDate,
        incorporationDate: formData.incorporationDate,
        businessSectorCode: formData.businessSectorCode,
        businessActivity: formData.businessActivity,
        businessType: formData.businessType,
        businessDescription: formData.businessDescription,
        businessPurpose: formData.businessPurpose,
        industryType: formData.industryType,
        companyEmail: formData.companyEmail,
        companyPhoneCountryCode: formData.companyPhoneCountryCode,
        companyPhoneNumber: formData.companyPhoneNumber,
        companyPhoneExtension: formData.companyPhoneExtension,
        addressLine1: formData.addressLine1,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        country: formData.country,
        website: formData.website,

        // Representatives data (documents already uploaded via Step 6)
        representatives: formData.representatives.map(rep => {
          const { documents, ...repData } = rep;
          return repData;
        })
      };

      console.log('📤 Sending request to API...');
      console.log('📋 Form data summary:', {
        companyName: formData.companyName,
        representativesCount: formData.representatives.length,
        hasBusinessRegistration: !!formData.businessRegistration,
        hasArticlesOfIncorporation: !!formData.articlesOfIncorporation,
        hasBusinessUtilityBill: !!formData.businessUtilityBill
      });

      const response = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      console.log('📨 Response status:', response.status);

      if (response.ok) {
        // Success!
        const result = await response.json();
        console.log('✅ Corporate onboarding submitted successfully:', result);

        // Clear ALL storage immediately to prevent SUBUTAI from activating
        localStorage.clear();
        sessionStorage.clear();

        // Show success message
        alert(getTranslation('success_message', language));

        // Redirect to login immediately (no SUBUTAI for corporate web form)
        window.location.replace('/login');
      } else {
        const error = await response.json();
        console.error('❌ Server error:', error);
        alert(error.error?.message || error.message || getTranslation('error_submitting', language));
      }
    } catch (error) {
      console.error('❌ Error submitting form:', error);
      alert(getTranslation('error_submitting', language));
    } finally {
      setLoading(false);
    }
  };

  // Function to finalize representatives from Step 6 and jump directly to Step 7
  const handleFinalizeAndContinue = () => {
    setRepresentativesFinalized(true);
    setCurrentStep(7); // Jump directly to Review (skip Step 4)
  };

  const renderStep = () => {
    const commonProps = {
      formData,
      updateFormData,
      language,
      errors,
    };

    switch (currentStep) {
      case 1:
        return <Step1CompanyInfo {...commonProps} />;
      case 2:
        return <Step2CompanyAddress {...commonProps} />;
      case 3:
        return (
          <Step3CompanyDocuments
            {...commonProps}
            onUploadBusinessRegistration={uploadBusinessRegistration}
            onUploadArticlesOfIncorporation={uploadArticlesOfIncorporation}
            onUploadBusinessUtilityBill={uploadBusinessUtilityBill}
          />
        );
      case 4:
        return (
          <Step4Representatives
            {...commonProps}
            representativesFinalized={representativesFinalized}
            setRepresentativesFinalized={setRepresentativesFinalized}
          />
        );
      case 5:
        return (
          <Step5RepresentativeForm
            {...commonProps}
            onNext={() => setCurrentStep(6)}
          />
        );
      case 6:
        return (
          <Step6RepresentativeDocuments
            {...commonProps}
            onBackToList={() => {
              // Solo resetear currentRepresentativeIndex, el useEffect se encarga del resto
              updateFormData({ currentRepresentativeIndex: -1 });
            }}
            uploadRepresentativeDocument={uploadRepresentativeDocument}
          />
        );
      case 7:
        return (
          <Step7ReviewAndSubmit
            {...commonProps}
            onSubmit={handleSubmit}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
          />
        );
      default:
        return null;
    }
  };

  const progressPercentage = Math.round((currentStep / 7) * 100);

  const handleLogout = () => {
    if (window.confirm(getTranslation('logout_confirm', language))) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  };

  // 🔄 MOSTRAR LOADER MIENTRAS CARGA EL IDIOMA
  if (languageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#B3A369]"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with logo and logout */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <img src={Logo} alt="BELOBABA" className="h-8" />
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B3A369]"
          >
            <svg
              className="mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            {getTranslation('logout_button', language)}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {getTranslation('step_of', language)
                .replace('{current}', currentStep.toString())
                .replace('{total}', '7')}
            </span>
            <span className="text-sm font-medium text-gray-700">
              {progressPercentage}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#B3A369] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          {renderStep()}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1 || loading}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              currentStep === 1 || loading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {getTranslation('previous_button', language)}
          </button>

          <button
            onClick={handleNext}
            disabled={loading || (currentStep === 4 && !representativesFinalized) || (currentStep === 7 && !termsAccepted) || !isStep3Complete()}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              loading || (currentStep === 4 && !representativesFinalized) || (currentStep === 7 && !termsAccepted) || !isStep3Complete()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#B3A369] text-white hover:bg-[#9A8B57]'
            }`}
          >
            {loading
              ? getTranslation('loading', language)
              : currentStep === 7
              ? getTranslation('submit_button', language)
              : getTranslation('next_button', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CorporateOnboarding;
